import { gqlFetcher } from '#features/api';
import { envConfig } from '#constants/envConfig';

import type { QualitySnapshot } from './qualityTracker';

export type QualityKind = 'CALL' | 'VOICE';

// Строкой, а не через graphql(): мутация ничего не выбирает из union и возвращает голый Boolean —
// тот же приём, что у `voice.room` в соседней папке.
const reportQualityMutation = `
  mutation QualityReport(
    $kind: MediaQualityKind!
    $refId: UUID!
    $durationSeconds: Int
    $poorSeconds: Int
    $lostSeconds: Int
    $reconnects: Int
    $playbackBlocked: Boolean
    $spoke: Boolean
    $micDenied: Boolean
    $disconnectReason: String
    $platform: String
  ) {
    quality {
      report(
        kind: $kind
        refId: $refId
        durationSeconds: $durationSeconds
        poorSeconds: $poorSeconds
        lostSeconds: $lostSeconds
        reconnects: $reconnects
        playbackBlocked: $playbackBlocked
        spoke: $spoke
        micDenied: $micDenied
        disconnectReason: $disconnectReason
        platform: $platform
      )
    }
  }
`;

/**
 * Метка платформы вида `win-chrome`, `mac-safari`, `android-app`.
 *
 * Раньше весь веб приезжал одним словом `web` — то есть Windows, macOS и Linux со всеми
 * браузерами разом. Именно там и живут различия, ради которых эта телеметрия заводилась: Safari
 * требует пользовательского жеста на воспроизведение и на getUserMedia, Firefox иначе отдаёт
 * устройства. С одной меткой «web» такой перекос увидеть было нельзя в принципе.
 *
 * Колонка в базе — varchar(16), поэтому обе части короткие, а всё неузнанное сворачивается
 * в `other`: длинная метка обрезалась бы на стороне сервера и била бы по группировке.
 */
export const detectPlatform = (
  isNative: boolean,
  userAgent: string,
  brands?: { brand: string }[]
): string => {
  const ua = userAgent.toLowerCase();
  const ios = /iphone|ipad|ipod/.test(ua);

  if (isNative) {
    return ios ? 'ios-app' : 'android-app';
  }

  const os = ios
    ? 'ios'
    : /android/.test(ua)
      ? 'android'
      : /windows/.test(ua)
        ? 'win'
        : /mac os|macintosh/.test(ua)
          ? 'mac'
          : /linux|x11/.test(ua)
            ? 'linux'
            : 'other';

  // Chromium-браузеры врут в userAgent друг про друга (Edge пишет и Chrome, и Safari), поэтому
  // порядок проверок здесь — от самого специфичного к самому общему, а не алфавитный.
  // userAgentData надёжнее и проверяется первым, но есть только в Chromium.
  const brandNames = (brands ?? []).map((b) => b.brand.toLowerCase()).join(' ');
  const browser = /edg/.test(ua) || /microsoft edge/.test(brandNames)
    ? 'edge'
    : /opr\/|opera/.test(ua) || /opera/.test(brandNames)
      ? 'opera'
      : /yabrowser/.test(ua)
        ? 'yandex'
        : /firefox|fxios/.test(ua)
          ? 'firefox'
          : /chrome|crios|chromium/.test(ua)
            ? 'chrome'
            : /safari/.test(ua)
              ? 'safari'
              : 'other';

  return `${os}-${browser}`;
};

export const platform = () =>
  detectPlatform(
    Boolean(envConfig.IS_NATIVE),
    typeof navigator === 'undefined' ? '' : navigator.userAgent,
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } })
          .userAgentData?.brands
  );

/**
 * Отправка сводки о качестве завершённого сеанса.
 *
 * Fire-and-forget во всех смыслах: ошибка проглатывается молча и наружу не поднимается. Сеанс уже
 * закончился, чинить нечего, а показать человеку «не удалось отправить статистику» — значит
 * превратить невидимую вещь в раздражающую. Телеметрия не стоит сломанного звонка.
 */
export const reportQuality = async (
  kind: QualityKind,
  refId: string,
  snapshot: QualitySnapshot,
  /** true при выгрузке страницы: без keepalive браузер отменит запрос вместе с документом. */
  keepalive = false
): Promise<void> => {
  try {
    await gqlFetcher(
      reportQualityMutation,
      {
        kind,
        refId,
        durationSeconds: snapshot.durationSeconds,
        poorSeconds: snapshot.poorSeconds,
        lostSeconds: snapshot.lostSeconds,
        reconnects: snapshot.reconnects,
        playbackBlocked: snapshot.playbackBlocked,
        spoke: snapshot.spoke,
        micDenied: snapshot.micDenied,
        disconnectReason: snapshot.disconnectReason,
        platform: platform(),
      },
      // silent всегда: сводку никто не ждёт, а падает она ровно там, где сеть уже плохая —
      // то есть каждый интересный нам случай засорял бы Sentry вместо того, чтобы попасть в отчёт.
      { silent: true, ...(keepalive ? { keepalive: true } : {}) }
    );
  } catch {
    // Осознанно пусто, см. комментарий к функции.
  }
};
